/**
 * utils/sessionAnalytics.js
 *
 * CLEAN LEAD ANALYTICS
 * Client-facing summarized insights
 */

/**
 * -----------------------------------------
 * HELPERS
 * -----------------------------------------
 */

const toMs = (timestamp) =>
  new Date(timestamp).getTime();

const msToSeconds = (ms) =>
  Math.floor(ms / 1000);

const getDurationSeconds = (
  start,
  end
) =>
  msToSeconds(toMs(end) - toMs(start));

/**
 * -----------------------------------------
 * EVENT FILTERS
 * -----------------------------------------
 */

const getSessionEvents = (events) =>
  events.filter((e) =>
    [
      "session_started",
      "session_ended",
    ].includes(e.event_name)
  );

const getViewEvents = (events) =>
  events.filter(
    (e) =>
      e.event_name === "view_changed"
  );

const getFilterEvents = (events) =>
  events.filter((e) =>
    [
      "filter_location_changed",
      "filter_budget_changed",
    ].includes(e.event_name)
  );

const getPropertyEvents = (events) =>
  events.filter(
    (e) =>
      e.event_name === "property_viewed"
  );

/**
 * -----------------------------------------
 * SESSION SUMMARY
 * -----------------------------------------
 */

const calculateSession = (
  sessionEvents
) => {
  const start = sessionEvents.find(
    (e) =>
      e.event_name ===
      "session_started"
  );

  const end = sessionEvents.find(
    (e) =>
      e.event_name ===
      "session_ended"
  );

  if (!start || !end) {
    return null;
  }

  const duration =
    getDurationSeconds(
      start.event_timestamp,
      end.event_timestamp
    );

  return {
    startedAt:
      start.event_timestamp,

    endedAt:
      end.event_timestamp,

    duration: formatDuration(duration),
  };
};

/**
 * -----------------------------------------
 * VIEW ANALYTICS
 * -----------------------------------------
 */

const calculateViewAnalytics = ({
  sessionEvents,
  viewEvents,
}) => {
  const sessionStart =
    sessionEvents.find(
      (e) =>
        e.event_name ===
        "session_started"
    );

  const sessionEnd =
    sessionEvents.find(
      (e) =>
        e.event_name ===
        "session_ended"
    );

  if (!sessionStart || !sessionEnd) {
    return {
      totalChatTime: 0,
      totalProjectsTime: 0,
      dominantView: null,
    };
  }

  const timeline = [
    {
      event_name: "view_changed",

      event_data: {
        view: "chat",
      },

      event_timestamp:
        sessionStart.event_timestamp,
    },

    ...viewEvents,

    {
      event_name: "session_ended",

      event_timestamp:
        sessionEnd.event_timestamp,
    },
  ];

  const totals = {
    chat: 0,
    projects: 0,
  };

  timeline.forEach(
    (current, index) => {
      const next =
        timeline[index + 1];

      if (
        !next ||
        !current.event_data?.view
      ) {
        return;
      }

      const seconds =
        getDurationSeconds(
          current.event_timestamp,
          next.event_timestamp
        );

      totals[
        current.event_data.view
      ] =
        (totals[
          current.event_data.view
        ] || 0) + seconds;
    }
  );

  return {
    totalChatTime:
      totals.chat || 0,

    totalProjectsTime:
      totals.projects || 0,

    dominantView:
      totals.projects >
      totals.chat
        ? "projects"
        : "chat",
  };
};

/**
 * -----------------------------------------
 * BUYER INTENT
 * -----------------------------------------
 */

const calculateBuyerIntent = (
  filterEvents
) => {
  const locationEvents =
    filterEvents.filter(
      (e) =>
        e.event_name ===
        "filter_location_changed"
    );

  const budgetEvents =
    filterEvents.filter(
      (e) =>
        e.event_name ===
        "filter_budget_changed"
    );

  /**
   * LOCATION
   */

  const lastLocation =
    locationEvents[
      locationEvents.length - 1
    ];

  /**
   * BUDGET
   */

  const firstBudget =
    budgetEvents[0];

  const lastBudget =
    budgetEvents[
      budgetEvents.length - 1
    ];

  let budgetFlexibility =
    "Stable";

  if (
    firstBudget &&
    lastBudget
  ) {
    if (
      lastBudget.event_data
        .budget_max >
      firstBudget.event_data
        .budget_max
    ) {
      budgetFlexibility =
        "Increased";
    }

    if (
      lastBudget.event_data
        .budget_max <
      firstBudget.event_data
        .budget_max
    ) {
      budgetFlexibility =
        "Decreased";
    }
  }

  return {
    preferredLocation:
      lastLocation?.event_data
        ?.location || null,

    finalBudget: lastBudget
      ? {
          min:
            lastBudget.event_data
              .budget_min,

          max:
            lastBudget.event_data
              .budget_max,
        }
      : null,

    budgetFlexibility,

    budgetChanged:
      budgetEvents.length > 1,
  };
};


const calculatePropertyInterest = (
  events
) => {
  const propertyEvents =
    getPropertyEvents(events);

  return propertyEvents.map(
    (event, index) => {
      const nextEvent =
        events[
          events.indexOf(event) + 1
        ];

      const dwellTime =
        nextEvent
          ? getDurationSeconds(
              event.event_timestamp,
              nextEvent.event_timestamp
            )
          : 0;

      return {
        property:
        event.event_data?.name ||
        "Unknown",

        viewedAt:
          event.event_timestamp,

        dwellTimeSeconds:
          dwellTime,

      };
    }
  );
};

/**
 * Convert seconds into
 * readable duration
 */

export const formatDuration = (
  totalSeconds
) => {
  const minutes = Math.floor(
    totalSeconds / 60
  );

  const seconds =
    totalSeconds % 60;

  /**
   * Examples:
   * 65  => 1m 5s
   * 10  => 10s
   * 120 => 2m 0s
   */

  if (minutes <= 0) {
    return `${seconds}s`;
  }

  return `${minutes}m ${seconds}s`;
};


/**
 * -----------------------------------------
 * MAIN ANALYTICS
 * -----------------------------------------
 */

export const generateLeadAnalytics = (
  events
) => {
  /**
   * SORT EVENTS
   */

  const sortedEvents = [
    ...events,
  ].sort(
    (a, b) =>
      toMs(a.event_timestamp) -
      toMs(b.event_timestamp)
  );

  /**
   * FILTER EVENTS
   */

  const sessionEvents =
    getSessionEvents(
      sortedEvents
    );

  const viewEvents =
    getViewEvents(
      sortedEvents
    );

  const filterEvents =
    getFilterEvents(
      sortedEvents
    );

  /**
   * ANALYTICS
   */

  const session =
    calculateSession(
      sessionEvents
    );

  const engagement =
    calculateViewAnalytics({
      sessionEvents,
      viewEvents,
    });

  const buyerIntent =
    calculateBuyerIntent(
      filterEvents
    );

  const propertyInterest =
    calculatePropertyInterest(
      sortedEvents
    );

  /**
   * MOST INTERESTED PROPERTY
   */

  const topProperty =
    [...propertyInterest].sort(
      (a, b) =>
        b.dwellTimeSeconds -
        a.dwellTimeSeconds
    )[0] || null;



  /**
   * FINAL RESPONSE
   */

  return {
    session,

    engagement,

    buyerIntent,

    propertyInterest,

    summary: {
      viewedProperties:
        propertyInterest.length,

      mostInterestedProperty:
        topProperty?.property ||
        null,

      highestDwellTime:
        topProperty
          ?.dwellTimeSeconds || 0,
    },
  };
};